// shell/panes/pane-actions.ts
// Title-row action buttons — the shared renderer behind `PaneType.tabBarActions`.
//
// A pane's own verbs (Reply, Send, Download, Edit…) belong in the pane's title
// row, next to its close button, not in the shell's global action bar: the
// buttons then travel with the pane (side by side, popped out), and nothing
// depends on which pane happens to be focused. Pane types describe their verbs
// as `PaneActionDescriptor`s and hand the result of `renderPaneActions()` back
// from `tabBarActions`. Secondary verbs fold into one trailing ⋯ menu.
//
// A row is not only verbs. A pane may also need a live status chip (the
// `status` option), and an editor's title row carries display-state controls
// as well: a menu of choices, a state toggle, a view-mode switch, a scope
// select, a size slider, or an element the pane built and cached itself.
// Those are the non-button `kind`s of `PaneControlDescriptor`.

import { createBadge, createButton, createSlider } from '../utils/pane-ui';
import type { BadgeVariant } from '../utils/pane-ui';
import { createDropdownMenu } from '../components/dropdown-menu';
import type { DropdownMenuItem } from '../components/dropdown-menu';
import { createSelectControl } from '../components/select-control';
import type { SelectControlOption } from '../components/select-control';
import { createSegmentedControl } from '../components/segmented-control';
import type { SegmentedControlOption } from '../components/segmented-control';
import { icons } from '../utils/icons';

export type PaneActionVariant = 'default' | 'primary' | 'danger';

/** Which primitive a descriptor renders as. Omitted means `'button'`. */
export type PaneActionKind =
    | 'button' | 'menu' | 'toggle' | 'slider' | 'select' | 'segmented' | 'element';

/** Fields every descriptor carries. */
export interface PaneControlCommon {
    /** Stable identifier; lands on the element as `data-action-key`. */
    key: string;
    /** Tooltip. When the control is disabled, this should say why. */
    title?: string;
    disabled?: boolean;
}

/**
 * A verb: one button. This is the default kind — a descriptor without `kind`
 * is a button, which is why `PaneActionDescriptor` is the name the rest of the
 * codebase uses for a plain plan of verbs.
 */
export interface PaneActionDescriptor extends PaneControlCommon {
    kind?: 'button';
    label: string;
    /**
     * Optional icon (text/emoji or an SVG element), prepended to the label.
     * With `label: ''` the button renders icon-only, and `title` is then its
     * accessible name as well as its tooltip — so an icon-only control MUST
     * carry one. Ignored inside the ⋯ menu, whose items are label-only.
     */
    icon?: string | Element;
    variant?: PaneActionVariant;
    /** Fold into the trailing ⋯ menu instead of rendering an inline button. */
    overflow?: boolean;
    /**
     * A write is in flight. Renders exactly as a disabled button does —
     * `disabled`, the label replaced by `busyLabel`, `busyLabel` as the
     * tooltip — inline and inside the ⋯ menu alike. It is sugar over
     * `disabled` + `title`, never a second mechanism: the pane still re-plans
     * from the server's answer when the write settles.
     */
    busy?: boolean;
    /** What the button says while `busy` ("Updating the allowlist…"). */
    busyLabel?: string;
    /**
     * Runs the verb. It receives the element it was rendered into — the
     * inline button, or the ⋯ trigger for an overflow item, which is the
     * element that is actually IN the row — so a verb that opens a popover
     * can anchor it to its own button instead of re-scanning the row by
     * `data-action-key`. Ignore the argument for anything else.
     */
    execute: (el?: HTMLElement) => void | Promise<void>;
}

/** Alias for symmetry with the other kind names. */
export type PaneButtonDescriptor = PaneActionDescriptor;

/** One choice inside a `menu` descriptor — the ⋯ menu's item shape. */
export type PaneMenuItem = DropdownMenuItem;

/** One trigger, N choices — a brand chooser, a "+ new" menu, a theme picker. */
export interface PaneMenuDescriptor extends PaneControlCommon {
    kind: 'menu';
    /** The trigger's label. A menu always renders labelled, never as a bare icon. */
    label: string;
    items: readonly PaneMenuItem[];
}

/** A sticky on/off control: show deleted, contents pane, crop mode. */
export interface PaneToggleDescriptor extends PaneControlCommon {
    kind: 'toggle';
    label: string;
    /** As on a button: icon-only when `label` is `''`, `title` then required. */
    icon?: string | Element;
    active: boolean;
    /** Receives the value the control has just flipped TO. */
    onToggle: (next: boolean) => void | Promise<void>;
}

/** A continuous display setting: thumbnail size, zoom. */
export interface PaneSliderDescriptor extends PaneControlCommon {
    kind: 'slider';
    min: number;
    max: number;
    step?: number;
    value: number;
    /** Width of the range input (number = px, string = CSS length). */
    width?: number | string;
    /** Fires continuously while dragging. */
    onInput?: (value: number) => void;
    /** Fires on commit (mouseup, blur, Enter). */
    onChange: (value: number) => void;
}

export type PaneSelectOption = SelectControlOption;

/** A scope applied on change: a version, a profile, a frame rate. */
export interface PaneSelectDescriptor extends PaneControlCommon {
    kind: 'select';
    value: string;
    options: readonly PaneSelectOption[];
    onChange: (value: string) => void;
}

export type PaneSegmentedOption = SegmentedControlOption;

/** A one-of-N view switch: table|grid, Current|At-read-time. */
export interface PaneSegmentedDescriptor extends PaneControlCommon {
    kind: 'segmented';
    value: string;
    options: readonly PaneSegmentedOption[];
    onChange: (value: string) => void;
}

/**
 * The escape hatch: an element the PANE built and cached.
 *
 * It deliberately takes an element and not a factory. Both surfaces clear the
 * `.cs-tab-bar-actions` container and re-run `tabBarActions` on every refresh,
 * so anything that owns a subscription, a timer or popover state must be built
 * ONCE by the pane (on `render`, kept on its state object / `pane.data`) and
 * only re-attached here — otherwise every re-plan leaks the old one. The plan
 * stays testable because the descriptor list is still data.
 */
export interface PaneElementDescriptor {
    kind: 'element';
    key: string;
    element: HTMLElement;
}

/**
 * Everything a title row can hold. A plan of plain verbs types as
 * `PaneActionDescriptor[]`; a row that mixes in widgets types as
 * `PaneControlDescriptor[]`.
 */
export type PaneControlDescriptor =
    | PaneActionDescriptor
    | PaneMenuDescriptor
    | PaneToggleDescriptor
    | PaneSliderDescriptor
    | PaneSelectDescriptor
    | PaneSegmentedDescriptor
    | PaneElementDescriptor;

/**
 * A live, non-clickable fact about the pane's subject: a connection state, a
 * publication state, an expiring certificate, "waiting for key". It renders as
 * a badge at the LEFT end of the title row and is refreshed by the same
 * `refreshPaneActions` call as the buttons. A verb never goes here.
 */
export interface PaneStatusDescriptor {
    /** Stable identifier; lands on the badge as `data-status-key`. */
    key: string;
    text: string;
    variant?: BadgeVariant;
    /** Tooltip — the long form of a text that has to stay short. */
    title?: string;
}

export interface RenderPaneActionsOptions {
    /** Tooltip of the ⋯ trigger (default "More actions"). */
    overflowTitle?: string;
    /**
     * Button size. Title rows use the default `'small'`; a plan rendered into
     * a pane BODY (a settings group's action row) should pass `'medium'` so it
     * matches the regular buttons around it. Applied to every kind whose
     * primitive has a size (button, toggle, select, segmented).
     */
    size?: 'small' | 'medium';
    /** Status chips, rendered before the buttons. */
    status?: readonly PaneStatusDescriptor[];
}

const VARIANT_CLASS: Record<PaneActionVariant, 'default' | 'yes' | 'no'> = {
    default: 'default',
    primary: 'yes',
    danger: 'no',
};

/** A descriptor without `kind` is a button. */
function isButtonDescriptor(action: PaneControlDescriptor): action is PaneActionDescriptor {
    return action.kind === undefined || action.kind === 'button';
}

/** `busy` renders through the same three outputs a disabled button uses. */
function buttonState(action: PaneActionDescriptor): {
    label: string; title: string | undefined; disabled: boolean;
} {
    if (!action.busy) {
        return { label: action.label, title: action.title, disabled: !!action.disabled };
    }
    return {
        label: action.busyLabel ?? action.label,
        title: action.busyLabel ?? action.title,
        disabled: true,
    };
}

/** An icon-only control has no visible text: its `title` is its accessible name too. */
function applyIconOnlyName(
    btn: HTMLElement, label: string, icon: string | Element | undefined, title: string | undefined,
): void {
    if (icon && !label && title) btn.setAttribute('aria-label', title);
}

/**
 * Turn a plan into title-row elements: the status chips first, then one
 * control per inline descriptor, in plan order, then (when any BUTTON
 * overflows) a single ⋯ dropdown holding the rest. Returns [] for an empty
 * plan so a pane can hand it straight to the surface.
 */
export function renderPaneActions(
    actions: readonly PaneControlDescriptor[],
    options: RenderPaneActionsOptions = {},
): HTMLElement[] {
    const out: HTMLElement[] = [];
    const overflow: PaneActionDescriptor[] = [];
    const size = options.size ?? 'small';

    for (const status of options.status ?? []) {
        const badge = createBadge({
            text: status.text,
            variant: status.variant,
            className: 'cs-pane-status',
        });
        badge.dataset.statusKey = status.key;
        if (status.title) badge.setAttribute('title', status.title);
        out.push(badge);
    }

    for (const action of actions) {
        if (isButtonDescriptor(action)) {
            if (action.overflow) {
                overflow.push(action);
                continue;
            }
            const state = buttonState(action);
            const btn = createButton({
                label: state.label,
                icon: action.icon,
                size,
                variant: VARIANT_CLASS[action.variant ?? 'default'],
                disabled: state.disabled,
                title: state.title,
                className: 'cs-pane-action',
                onClick: () => { void action.execute(btn); },
            });
            btn.dataset.actionKey = action.key;
            applyIconOnlyName(btn, state.label, action.icon, state.title);
            out.push(btn);
            continue;
        }

        switch (action.kind) {
            case 'menu': {
                const menu = createDropdownMenu({
                    label: action.label,
                    title: action.title,
                    items: [...action.items],
                });
                const trigger = menu.element as HTMLButtonElement;
                trigger.classList.add('cs-pane-action');
                if (action.disabled) trigger.disabled = true;
                trigger.dataset.actionKey = action.key;
                out.push(trigger);
                break;
            }
            case 'toggle': {
                const btn = createButton({
                    label: action.label,
                    icon: action.icon,
                    size,
                    variant: 'state',
                    active: action.active,
                    disabled: action.disabled,
                    title: action.title,
                    className: 'cs-pane-action',
                    onClick: () => {
                        // The state variant leaves the active class to its
                        // caller once an onClick is supplied.
                        const next = !btn.classList.contains('cs-active');
                        btn.classList.toggle('cs-active', next);
                        void action.onToggle(next);
                    },
                });
                btn.dataset.actionKey = action.key;
                applyIconOnlyName(btn, action.label, action.icon, action.title);
                out.push(btn);
                break;
            }
            case 'slider': {
                const slider = createSlider({
                    min: action.min,
                    max: action.max,
                    step: action.step,
                    value: action.value,
                    disabled: action.disabled,
                    width: action.width,
                    title: action.title,
                    onInput: action.onInput,
                    onChange: action.onChange,
                });
                slider.element.classList.add('cs-pane-action');
                slider.element.dataset.actionKey = action.key;
                out.push(slider.element);
                break;
            }
            case 'select': {
                const select = createSelectControl({
                    value: action.value,
                    options: action.options,
                    disabled: action.disabled,
                    title: action.title,
                    size,
                    className: 'cs-pane-action',
                    onChange: action.onChange,
                });
                select.dataset.actionKey = action.key;
                out.push(select);
                break;
            }
            case 'segmented': {
                const segmented = createSegmentedControl({
                    value: action.value,
                    options: action.options,
                    disabled: action.disabled,
                    title: action.title,
                    size,
                    className: 'cs-pane-action',
                    onChange: action.onChange,
                });
                segmented.element.dataset.actionKey = action.key;
                out.push(segmented.element);
                break;
            }
            case 'element': {
                action.element.dataset.actionKey = action.key;
                out.push(action.element);
                break;
            }
        }
    }

    if (overflow.length > 0) {
        const menu = createDropdownMenu({
            icon: icons.more(),
            title: options.overflowTitle ?? 'More actions',
            // An overflow item has no element of its own in the row — the
            // menu is torn down on close — so its verb is handed the ⋯
            // trigger, which is what a popover would anchor to anyway.
            items: overflow.map((action): PaneMenuItem => {
                const state = buttonState(action);
                return {
                    label: state.label,
                    disabled: state.disabled,
                    title: state.title,
                    onClick: () => { void action.execute(menu.element); },
                };
            }),
        });
        menu.element.classList.add('cs-pane-actions-more');
        out.push(menu.element);
    }

    return out;
}
